import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/integrations/auth/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Building2, Loader2, CheckCircle, KeyRound } from "lucide-react";
import logoLivemotion from "@/assets/logo-livemotion.png";
import { toast } from "@/hooks/use-toast";
import {
  isNetworkError,
  friendlyAuthError,
  friendlyNetworkError,
} from "@/lib/error-utils";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const redirectTo = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Wait for auth status to load before showing login form
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Already authenticated — redirect to where the user was heading
  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: "Reset failed",
          description: friendlyAuthError(error),
          variant: "destructive",
        });
        return;
      }

      setResetEmailSent(true);
      toast({
        title: "Reset link sent!",
        description: "Check your email for the password reset link.",
      });
    } catch (error: unknown) {
      console.error("Password reset error:", error);
      toast({
        title: "Server unavailable",
        description: isNetworkError(error)
          ? friendlyNetworkError()
          : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await authClient.signIn.magicLink({
        email: email.trim(),
        callbackURL: redirectTo,
      });

      if (error) {
        toast({
          title: "Magic link failed",
          description: friendlyAuthError(error),
          variant: "destructive",
        });
        return;
      }

      setMagicLinkSent(true);
      toast({
        title: "Magic link sent!",
        description: "Check your email for the login link.",
      });
    } catch (error: unknown) {
      console.error("Magic link error:", error);
      toast({
        title: "Server unavailable",
        description: isNetworkError(error)
          ? friendlyNetworkError()
          : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast({
        title: "Required fields",
        description: "Please enter email and password.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await authClient.signIn.email({
        email: email.trim(),
        password,
      });
      if (error) {
        toast({
          title: "Sign in failed",
          description: friendlyAuthError(error),
          variant: "destructive",
        });
        return;
      }
      navigate(redirectTo);
    } catch (error: unknown) {
      console.error("Auth error:", error);
      toast({
        title: "Server unavailable",
        description: isNetworkError(error)
          ? friendlyNetworkError()
          : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftSSO = async () => {
    setIsLoading(true);
    try {
      const { error } = await authClient.signIn.social({
        provider: "microsoft",
        callbackURL: redirectTo,
      });
      if (error) {
        toast({
          title: "SSO failed",
          description: friendlyAuthError(error),
          variant: "destructive",
        });
        setIsLoading(false);
      }
    } catch (error: unknown) {
      console.error("Microsoft SSO error:", error);
      toast({
        title: "Server unavailable",
        description: isNetworkError(error)
          ? friendlyNetworkError()
          : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src={logoLivemotion}
              alt="LiveMotion"
              className="h-14 object-contain"
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">LM Hub</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Sign in to manage your projects
          </p>
        </div>

        <Card className="border-border/50">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-lg">Sign in</CardTitle>
            <CardDescription>Choose your sign-in method below</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="employee" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="employee" className="text-sm">
                  <Building2 className="h-4 w-4 mr-2" />
                  Employee
                </TabsTrigger>
                <TabsTrigger value="freelancer" className="text-sm">
                  <Mail className="h-4 w-4 mr-2" />
                  Freelancer
                </TabsTrigger>
              </TabsList>

              <TabsContent value="employee" className="space-y-4">
                {showForgotPassword ? (
                  resetEmailSent ? (
                    <div className="text-center py-6 space-y-3">
                      <CheckCircle className="h-12 w-12 text-success mx-auto" />
                      <div>
                        <p className="font-medium">Check your email!</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          We've sent a password reset link to{" "}
                          <strong>{email}</strong>
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setResetEmailSent(false);
                          setEmail("");
                        }}
                        className="text-sm"
                      >
                        Back to sign in
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Enter your email address and we'll send you a link to
                        reset your password.
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="reset-email">Email address</Label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isLoading}
                          autoComplete="email"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        Send reset link
                      </Button>
                      <div className="text-center">
                        <Button
                          type="button"
                          variant="link"
                          className="text-sm text-muted-foreground"
                          onClick={() => {
                            setShowForgotPassword(false);
                            setEmail("");
                          }}
                        >
                          Back to sign in
                        </Button>
                      </div>
                    </form>
                  )
                ) : showEmailPassword ? (
                  <form onSubmit={handleEmailPassword} className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Sign in with your employee credentials
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="emp-email">Email address</Label>
                      <Input
                        id="emp-email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="emp-password">Password</Label>
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 h-auto text-xs text-muted-foreground"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot password?
                        </Button>
                      </div>
                      <Input
                        id="emp-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                        autoComplete="current-password"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <KeyRound className="h-4 w-4 mr-2" />
                      )}
                      Sign in
                    </Button>
                    <div className="flex justify-end text-sm">
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 h-auto text-muted-foreground"
                        onClick={() => {
                          setShowEmailPassword(false);
                          setEmail("");
                          setPassword("");
                        }}
                      >
                        Use Microsoft SSO
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Sign in with your company Microsoft 365 account for full
                      access.
                    </p>
                    <Button
                      onClick={handleMicrosoftSSO}
                      className="w-full"
                      size="lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <svg
                          className="h-5 w-5 mr-2"
                          viewBox="0 0 21 21"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="9"
                            height="9"
                            fill="#F25022"
                          />
                          <rect
                            x="11"
                            y="1"
                            width="9"
                            height="9"
                            fill="#7FBA00"
                          />
                          <rect
                            x="1"
                            y="11"
                            width="9"
                            height="9"
                            fill="#00A4EF"
                          />
                          <rect
                            x="11"
                            y="11"
                            width="9"
                            height="9"
                            fill="#FFB900"
                          />
                        </svg>
                      )}
                      Sign in with Microsoft
                    </Button>
                    <div className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        className="text-sm text-muted-foreground"
                        onClick={() => setShowEmailPassword(true)}
                      >
                        Use email & password instead
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="freelancer" className="space-y-4">
                {magicLinkSent ? (
                  <div className="text-center py-6 space-y-3">
                    <CheckCircle className="h-12 w-12 text-success mx-auto" />
                    <div>
                      <p className="font-medium">Check your email!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        We've sent a magic link to <strong>{email}</strong>
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setMagicLinkSent(false);
                        setEmail("");
                      }}
                      className="text-sm"
                    >
                      Use a different email
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleMagicLink} className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Enter your email to receive a magic link for instant
                      access.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      Send magic link
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          By signing in, you agree to our terms of service and privacy policy.
        </p>
      </div>
    </div>
  );
}
